import { invokeS3Gateway } from './s3EdgeClient';
import { trackOperation } from '../utils/s3OperationCounter';

const PROJECT_DOCS_TOP = 'project-documents';

/** Matches s3-gateway `project-documents/{projectId}` (no trailing slash). */
export function projectDocumentsRootPath(projectId: string | number): string {
  return `${PROJECT_DOCS_TOP}/${String(projectId)}`;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresIn: number;
}

export interface S3Item {
  key: string;
  name: string;
  size: number;
  lastModified: Date;
  isFolder: boolean;
  url?: string;
  path: string;
}

const cache = {
  items: {} as Record<string, CacheEntry<S3Item[]>>,
  urls: {} as Record<string, CacheEntry<string>>,
};

function mapGatewayItem(raw: Record<string, unknown>): S3Item {
  return {
    key: String(raw.key ?? ''),
    name: String(raw.name ?? ''),
    size: Number(raw.size ?? 0),
    lastModified: new Date(String(raw.lastModified ?? Date.now())),
    isFolder: !!raw.isFolder,
    path: String(raw.path ?? ''),
    url: raw.url !== undefined ? String(raw.url) : undefined,
  };
}

const getCached = (
  cacheType: 'items',
  key: string,
): S3Item[] | null => {
  const entry = cache[cacheType][key];
  if (!entry) return null;
  if (Date.now() - entry.timestamp > entry.expiresIn) {
    delete cache[cacheType][key];
    return null;
  }
  return entry.data;
};

const setCached = (
  cacheType: 'items',
  key: string,
  data: S3Item[],
  expiresIn = 300000,
) => {
  cache[cacheType][key] = {
    data,
    timestamp: Date.now(),
    expiresIn,
  };
};

const getCachedUrl = (key: string): string | null => {
  const entry = cache.urls[key];
  if (!entry) return null;
  if (Date.now() - entry.timestamp > entry.expiresIn) {
    delete cache.urls[key];
    return null;
  }
  return entry.data;
};

const setCachedUrl = (key: string, url: string, expiresIn = 3540000) => {
  cache.urls[key] = {
    data: url,
    timestamp: Date.now(),
    expiresIn,
  };
};

/** Clears in-memory list/url caches for one project (keys are scoped as `${projectId}:${path}`). */
export function clearProjectExplorerCache(projectId: string): void {
  const p = `${projectId}:`;
  Object.keys(cache.items).forEach((k) => {
    if (k.startsWith(p)) delete cache.items[k];
  });
  Object.keys(cache.urls).forEach((k) => {
    if (k.startsWith(`${projectId}_`)) {
      delete cache.urls[k];
    }
  });
}

/** @deprecated Use clearProjectExplorerCache(projectId). */
export const clearCache = (_path?: string, projectId?: string) => {
  if (projectId) clearProjectExplorerCache(projectId);
  else {
    cache.items = {};
    cache.urls = {};
  }
};

export async function createInitialProjectFolders(projectId: number): Promise<void> {
  trackOperation('put');
  await invokeS3Gateway<{ ok?: boolean }>({
    action: 'projectCreateInitialStructure',
    payload: { projectId },
  });
}

export const listItems = async (
  projectId: string,
  path: string,
  useCache = true,
): Promise<S3Item[]> => {
  const cacheKey = `${projectId}:${path}`;
  if (useCache) {
    const cachedResult = getCached('items', cacheKey);
    if (cachedResult) return cachedResult;
  }

  trackOperation('list');
  const data = await invokeS3Gateway<{ items: Record<string, unknown>[] }>({
    action: 'projectListItems',
    payload: { projectId, path },
  });

  const items = (data.items ?? []).map(mapGatewayItem);

  if (useCache) {
    setCached('items', cacheKey, items);
  }

  return items;
};

/** Lists files across project root, key-plan/, and Documents/ (deduped). */
export async function listProjectDocumentFiles(
  projectId: string | number,
  useCache = true,
): Promise<S3Item[]> {
  const projectIdStr = String(projectId);
  const rootPath = projectDocumentsRootPath(projectIdStr);
  const subPaths = [
    rootPath,
    `${rootPath}/key-plan`,
    `${rootPath}/Documents`,
  ];

  const results = await Promise.all(
    subPaths.map((path) =>
      listItems(projectIdStr, path, useCache).catch(() => [] as S3Item[]),
    ),
  );

  const seen = new Set<string>();
  const files: S3Item[] = [];
  for (const items of results) {
    for (const item of items) {
      if (item.isFolder || seen.has(item.path)) continue;
      seen.add(item.path);
      files.push(item);
    }
  }
  return files;
}

export const getFileUrl = async (
  projectId: string,
  filePath: string,
  expirationInSeconds = 3600,
): Promise<string> => {
  const cacheKey = `${projectId}_${filePath}_${expirationInSeconds}`;
  const cachedUrl = getCachedUrl(cacheKey);
  if (cachedUrl) return cachedUrl;

  trackOperation('get');
  const data = await invokeS3Gateway<{ url: string }>({
    action: 'projectGetFileUrl',
    payload: {
      projectId,
      key: filePath,
      expirationInSeconds,
    },
  });

  const cacheExpirationMs = Math.max((expirationInSeconds - 60) * 1000, 60_000);
  setCachedUrl(cacheKey, data.url, cacheExpirationMs);
  return data.url;
};

export const getUploadUrl = async (
  projectId: string,
  filePath: string,
  contentType: string,
  expirationInSeconds = 3600,
): Promise<string> => {
  trackOperation('put');
  const data = await invokeS3Gateway<{ url: string }>({
    action: 'projectGetUploadUrl',
    payload: {
      projectId,
      key: filePath,
      contentType,
      expirationInSeconds,
    },
  });
  return data.url;
};

export const uploadFile = async (
  projectId: string,
  filePath: string,
  file: File,
): Promise<void> => {
  trackOperation('put');
  const uploadUrl = await getUploadUrl(
    projectId,
    filePath,
    file.type || 'application/octet-stream',
  );

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Upload failed with status: ${response.status}`);
  }

  clearProjectExplorerCache(projectId);
};

export const deleteFile = async (
  projectId: string,
  filePath: string,
): Promise<void> => {
  trackOperation('delete');
  await invokeS3Gateway<{ ok?: boolean }>({
    action: 'projectDeleteObject',
    payload: { projectId, key: filePath },
  });

  clearProjectExplorerCache(projectId);
};
